import React from 'react';
import { connect } from 'react-redux';
import CameraPlaceholder from './CameraPlaceholder';
import TileLoadingSpinner from './TileLoadingSpinner';
import CameraInfoOverlay from './CameraInfoOverlay';
import { buildApiUrl, buildEviewJpegUrl } from '../util/getServerInfo';
import { resolveMemberServerUrl } from '../actions/NavigationActions';
import { debugLog, debugWarn } from '../util/debug';
import { cameraDisplayLabel } from '../util/cameraLabel';

class CameraStreamContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: false,
            timestamp: '',
            enabled: false,
            imgError: false,
            imgLoaded: false,
            disconnectStream: false
        }

        this.updateHandler = 0;
        this._isMounted = false;
        this._imgPending = false; // Track whether current JPEG request is still in-flight
    }

    componentDidMount = () => {
        this._isMounted = true;
        // Start JPEG polling immediately — no stagger delay.
        // The browser handles connection pooling (6 concurrent per origin with keep-alive).
        // Stagger was causing 250ms-1s black screens on every mount.
        const cam = this.props.enabled;
        if (cam && cam.fEnable) {
            this.setState({
                enabled: true,
                loading: false,
                disconnectStream: false,
                imgError: false,
                imgLoaded: false
            }, () => {
                this.updateTimestamp();
                this.armFailoverWatchdog();
            });
        }
    }

    componentWillUnmount = () => {
        this._isMounted = false;
        this._imgPending = false;
        clearTimeout(this.updateHandler);
        clearTimeout(this._failoverWatchdog);
    }

    // First-frame failover watchdog (#86bb7phhh). eView member JPEGs go DIRECT to the member IP, so a
    // wrong URL guess to a black-holed IP (NAT hairpin) hangs the <img> for the browser's full TCP
    // timeout (tens of seconds) BEFORE onError fires — leaving a reachable member stuck on a spinner.
    // If no first frame arrives within 4s, proactively kick the guess-then-failover resolver (it probes
    // in ~2.5s and swaps to the member's other IP). Deduped per member in the thunk; a successful frame
    // (handleImageLoad) or unmount clears it. Zero cost when the guess is right (frame arrives fast).
    armFailoverWatchdog = () => {
        clearTimeout(this._failoverWatchdog);
        const cam = this.props.enabled;
        const isEview = this.props.useEviewLogin || this.props.fEview;
        if (!(isEview && cam && cam.bSerial && this.props.resolveMemberServerUrl)) return;
        this._failoverWatchdog = setTimeout(() => {
            // Skip if WS took over (paused): the member IS streaming, so a never-painted JPEG base must
            // NOT trigger a needless failover probe (that would flood probes in steady state). #86bb7phhh
            if (this._isMounted && !this.state.imgLoaded && !this.props.paused && this.props.resolveMemberServerUrl) {
                this.props.resolveMemberServerUrl(cam.bSerial);
            }
        }, 4000);
    };

    componentDidUpdate = (prevProps) => {
        // Detect camera change (slot reassignment)
        const prevCamId = prevProps.enabled?.bID;
        const newCamId = this.props.enabled?.bID;

        if (prevCamId !== newCamId) {
            debugLog('[CameraStreamContainer] Camera changed from', prevCamId, 'to', newCamId);
            clearTimeout(this.updateHandler);
            this._imgPending = false;

            if (this.props.enabled?.fEnable) {
                this.setState({
                    enabled: true,
                    loading: false,
                    disconnectStream: false,
                    imgError: false,
                    imgLoaded: false,
                    timestamp: this.props.nvrTimestampShort
                }, () => {
                    this.updateTimestamp();
                    this.armFailoverWatchdog();
                });
            } else {
                this.setState({
                    enabled: false,
                    disconnectStream: true
                });
            }
            return;
        }

        // Pause/resume polling when WS takes over / gives back
        if (prevProps.paused !== this.props.paused) {
            if (this.props.paused) {
                // WS is streaming — stop JPEG polling to save bandwidth, and cancel the failover watchdog
                // (a WS-upgraded tile is reachable; its JPEG base may never paint, which must not trip failover). #86bb7phhh
                clearTimeout(this.updateHandler);
                clearTimeout(this._failoverWatchdog);
            } else {
                // WS gave back — resume JPEG polling immediately
                this.updateTimestamp();
            }
        }
    }

    updateTimestamp = () => {
        if(this._isMounted && this.props.isLoggedIn && !this.state.disconnectStream && !this.props.paused) {
            // Don't fire a new request if the previous one is still in-flight.
            // With 16 cameras and the browser's 6-connection limit, blind 1s polling
            // causes request stampeding: queued requests get cancelled by the next
            // polling cycle, starving most cameras. Instead, wait for completion.
            if (this._imgPending) {
                // Safety: if pending for >8s, the browser likely cancelled the request
                // during a view switch (no onLoad/onError fires). Reset and retry.
                var now = Date.now();
                if (this._imgPendingSince && (now - this._imgPendingSince) > 8000) {
                    this._imgPending = false;
                    this._imgPendingSince = 0;
                } else {
                    this.updateHandler = setTimeout(() => { this.updateTimestamp(); this.updateHandler = 0 }, 200);
                    return;
                }
            }

            const retryInterval = this.state.imgError ? 5000 : 1000;

            this._imgPending = true;
            this._imgPendingSince = Date.now();
            this.setState({
                timestamp: this.props.nvrTimestampShort
            }, () => {
                if (!this._isMounted) return;
                this.props.isLoggedIn ?
                    this.updateHandler = setTimeout( () => { this.updateTimestamp(); this.updateHandler = 0 }, retryInterval ) :
                    clearTimeout(this.updateHandler)
            });
        }
    };

    formatTimestamp = (ts) => {
        if (!ts) return '';
        const d = new Date(ts);
        if (isNaN(d.getTime())) return '';
        let hour = ('0' + d.getHours()).slice(-2);
        let minute = ('0' + d.getMinutes()).slice(-2);
        let second = ('0' + d.getSeconds()).slice(-2);
        return hour + ':' + minute + ':' + second;
    };

    handleImageError = () => {
        this._imgPending = false;
        if (this._isMounted) {
            this.setState({ imgError: true, imgLoaded: false });
            // eView cross-NVR tile: the JPEG base failed to load from the guessed member URL. Kick the
            // guess-then-failover resolver so a wrong-IP guess (NAT hairpin) flips to the member's other
            // IP instead of stranding the tile on "failed". Idempotent per member (the thunk dedupes the
            // ~5s error retries). #86bb7phhh
            const cam = this.props.enabled;
            const isEview = this.props.useEviewLogin || this.props.fEview;
            if (isEview && cam && cam.bSerial && this.props.resolveMemberServerUrl) {
                this.props.resolveMemberServerUrl(cam.bSerial);
            }
        }
    };

    handleImageLoad = () => {
        this._imgPending = false;
        clearTimeout(this._failoverWatchdog);   // got a frame — the guess was right, cancel failover
        if (this._isMounted) {
            this.setState({ imgError: false, imgLoaded: true });
        }
    };

    handleContextMenu = (e) => {
        e.preventDefault();
        debugLog('[CameraStreamContainer] Right-click detected, slotIndex:', this.props.slotIndex, 'onContextMenu prop:', !!this.props.onContextMenu);
        if (this.props.onContextMenu && this.props.slotIndex !== undefined) {
            debugLog('[CameraStreamContainer] Calling parent onContextMenu handler');
            this.props.onContextMenu(e, this.props.slotIndex, this.props.enabled);
        } else {
            debugLog('[CameraStreamContainer] Missing onContextMenu or slotIndex - onContextMenu:', !!this.props.onContextMenu, 'slotIndex:', this.props.slotIndex);
        }
    };

    render() {
        const { enabled, conf, fSingle, sSess, camNum, useEviewLogin, fEview, serverSessions, username, password } = this.props;
        const isEviewMode = useEviewLogin || fEview;
        const isHighRes = conf === 'conf-1' || fSingle;

        // For cross-NVR cameras the child NVR doesn't honor the controller's
        // session token. Pass user/pass alongside so the JPEG URL falls back
        // to ?user=&pass= when serverInfo.sSess is null (matches Java dview).
        const fallbackAuth = { session: sSess, username, password };

        // Build JPEG URL based on mode and camera source
        let jpegUrl;
        if (isEviewMode && enabled && enabled.bSerial) {
            const eviewBaseUrl = buildEviewJpegUrl(enabled, isHighRes, serverSessions, fallbackAuth);
            if (eviewBaseUrl) {
                jpegUrl = eviewBaseUrl + this.state.timestamp;
            } else {
                // Fallback: try the camera's own serverUrl if it carries one
                // (legacy view payload), still with auth fallback.
                if (enabled.serverUrl) {
                    const baseUrl = enabled.serverUrl.replace(/\/JSON\/?$/, '');
                    const endpoint = isHighRes ? `/mpe/cam${enabled.bID}.jpg` : `/mpe/scam${enabled.bID}.jpg`;
                    const authQuery = (username && password)
                        ? `user=${encodeURIComponent(username)}&pass=${encodeURIComponent(password)}`
                        : `sess=${encodeURIComponent(sSess || '')}`;
                    jpegUrl = `${baseUrl}${endpoint}?${authQuery}&ts=${this.state.timestamp}`;
                } else {
                    // Last resort: local URL (won't reach a remote camera, but
                    // keeps the placeholder rendering instead of erroring out)
                    debugWarn('[CameraStreamContainer] No serverUrl for camera', enabled.bID, 'from NVR', enabled.bSerial);
                    jpegUrl = buildApiUrl(isHighRes ? '/mpe/cam' + enabled.bID + '.jpg?sess=' + sSess + '&ts=' + this.state.timestamp : '/mpe/scam' + enabled.bID + '.jpg?sess=' + sSess + '&ts=' + this.state.timestamp);
                }
            }
        } else {
            // dview mode - use standard URL building with camNum (slot-based)
            jpegUrl = buildApiUrl(isHighRes ? '/mpe/cam' + camNum + '.jpg?sess=' + sSess + '&ts=' + this.state.timestamp : '/mpe/scam' + camNum + '.jpg?sess=' + sSess + '&ts=' + this.state.timestamp);
        }

        // Shared Live/Playback label helper — eView "Server : Cam", local "id - Cam" (#86bb7phhh).
        const camName = enabled ? cameraDisplayLabel(enabled, { isEview: isEviewMode }) : `Cam ${camNum}`;

        // eView failover terminal state: the member answered on NEITHER of its candidate IPs
        // (resolveMemberServerUrl gave up) — say "Unable to connect", vs a transient "Connection Failed"
        // that a re-poll / in-flight failover may still clear. #86bb7phhh
        const memberSession = isEviewMode && enabled && enabled.bSerial ? serverSessions[enabled.bSerial] : null;
        const memberUnreachable = !!(memberSession && memberSession.urlUnreachable);

        return (
            <div style={{ float: 'left', height: this.props.height, width: this.props.width, overflow: 'hidden' }} onDoubleClick={ () => this.props.conf !== "conf-1" ? this.props.onDoubleClick() : null } onContextMenu={this.handleContextMenu}>
                { this.state.enabled && this.props.enabled !== 'false' && this.props.userCameras.indexOf(parseInt(this.props.camNum)) > -1 ?
                    <div style={{ position: 'relative', height: '100%', width: '100%', backgroundColor: '#000', overflow: 'hidden' }} onContextMenu={this.handleContextMenu}>
                        {/* Loading spinner while the first JPEG loads (and on camera switch, which
                            resets imgLoaded) — the same affordance as the cloud path, so eview/dview
                            cams read as "loading" instead of a bare placeholder (David 2026-08-15). */}
                        {!this.state.imgLoaded && !this.state.imgError && <TileLoadingSpinner />}
                        <img src={jpegUrl}
                            alt='camera_stream'
                            height={'100%'}
                            width={'100%'}
                            style={{
                                border: 'none',
                                visibility: this.state.imgLoaded && !this.state.imgError ? 'visible' : 'hidden'
                            }}
                            onError={this.handleImageError}
                            onLoad={this.handleImageLoad} />
                        {this.state.imgError && (
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                pointerEvents: 'none'
                            }}>
                                <span style={{
                                    background: 'rgba(0,0,0,0.6)',
                                    color: 'white',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontFamily: 'system-ui, sans-serif',
                                    userSelect: 'none'
                                }}>
                                    {memberUnreachable ? 'Unable to connect' : 'Connection Failed'}
                                </span>
                            </div>
                        )}
                        {/* The local/eView hybrid tile (VideoViewWs) renders ONE overlay for the whole
                            tile, so it passes showOverlay={false} here to avoid a stacked duplicate. Any
                            standalone use (no prop) keeps its own overlay (showOverlay!==false). #86bb7phhh */}
                        {this.props.showOverlay !== false && (
                          <CameraInfoOverlay
                            camName={camName}
                            timestamp={this.formatTimestamp(this.props.nvrTimestamp)}
                            streamType="jpg"
                          />
                        )}
                    </div>  :
                    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
                        <CameraPlaceholder />
                    </div>
                }
            </div>
        )
    }
}

const mapStateToProps = state => {
    const { sSess, sServerLiveMPE, isLoggedIn, username, password, useEviewLogin } = state.auth;
    const { sServer, cameras, fEview, serverSessions, bSerial } = state.server;
    const { conf } = state.config;
    const { fSingle } = state.video;
    const { userCameras } = state.user;
    const { nvrTimestamp, nvrTimestampShort} = state.utility;
    return {
        sSess,
        sServerLiveMPE,
        isLoggedIn,
        username,
        password,
        sServer,
        cameras,
        conf,
        fSingle,
        userCameras,
        nvrTimestamp,
        nvrTimestampShort,
        // eView mode support
        useEviewLogin,
        fEview,
        serverSessions: serverSessions || {},
        bSerial
    }
}

export default connect(mapStateToProps, { resolveMemberServerUrl })(CameraStreamContainer);
